/**
 * CR-7297: the ONE Export control on Supplier Performance.
 *
 * The page had two — "Export" in the filter bar (where the CR-6403 design puts
 * it, as on the dashboard) and "Export to Excel" beside the Matrix/Table
 * toggle. Round 1 made both write the view on screen; the toggle-side one is
 * now gone and the filter-bar one is the only route, so there is nothing left
 * to disagree. This hook decides, per view, what that control writes, when it
 * is unavailable and what its tooltip says:
 *
 *  - Table view: every supplier matching the filters (`exportTable`, which
 *    pulls all pages) → `Supplier_Performance_<date>.xlsx`. Busy while the
 *    fetch runs — on a large tenant that is tens of seconds — so a second click
 *    cannot start a second download.
 *  - Matrix view: the rows on screen, after the band legend and quick search →
 *    `Supplier_Performance_Matrix_FY<year>_<date>.xlsx`. No re-fetch: the page
 *    already holds the grid (usePerformanceMatrix).
 *
 * With nothing to export the control is disabled and its tooltip says why,
 * rather than downloading a header-only file.
 */
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { exportMatrix } from './matrix/exportMatrix';
import type { PerformanceMatrixState } from './matrix/usePerformanceMatrix';

interface UsePerformanceExportArgs {
  isMatrixView: boolean;
  matrix: PerformanceMatrixState;
  /** Writes the Table export (all pages); resolves once the file is written. */
  exportTable: () => Promise<void>;
  /** Rows the Table's request matched (server count; client count for suppliers). */
  tableCount: number;
  /** The Table is (re)loading — its count is not settled yet. */
  tableLoading: boolean;
}

export interface PerformanceExportControl {
  onExport: () => void;
  /** A Table export is being fetched/written. */
  exporting: boolean;
  /** Nothing to export, or the view is still loading (the button also disables while `exporting`). */
  disabled: boolean;
  /** Tooltip: what this click will write, or why it cannot. */
  title: string;
}

/**
 * The Export control's behaviour for the view on screen.
 *
 * @param args - the current view, the matrix state, the Table exporter and count
 * @returns handler + busy/disabled flags + tooltip for the one Export button
 * @example
 * const exportControl = usePerformanceExport({ isMatrixView, matrix, exportTable: handleExportExcel,
 *   tableCount: pagination.totalCount, tableLoading: loading });
 * <SupplierPerformanceFilters onExport={exportControl.onExport} exporting={exportControl.exporting} … />
 */
export function usePerformanceExport({
  isMatrixView, matrix, exportTable, tableCount, tableLoading,
}: UsePerformanceExportArgs): PerformanceExportControl {
  const [exporting, setExporting] = useState(false);
  // A ref as well as state: two clicks in the same tick both see state=false.
  const inFlight = useRef(false);

  const matrixCount = matrix.visibleRows.length;
  const matrixYear = matrix.yearLabel || String(matrix.year);

  let disabled: boolean;
  let title: string;
  if (isMatrixView) {
    disabled = matrix.loading || !!matrix.error || matrixCount === 0;
    title = matrix.loading
      ? 'Loading the matrix…'
      : matrix.error
        ? 'The matrix did not load, so there is nothing to export'
        : matrixCount === 0
          ? `No suppliers in the matrix for ${matrixYear} — nothing to export`
          : `Export the ${matrixCount} ${matrixCount === 1 ? 'supplier' : 'suppliers'} in the matrix (${matrixYear}) to Excel`;
  } else {
    disabled = tableLoading || tableCount === 0;
    title = exporting
      ? 'Exporting every supplier matching the filters…'
      : tableLoading
        ? 'Loading suppliers…'
        : tableCount === 0
          ? 'No suppliers match the filters — nothing to export'
          : 'Export every supplier matching the filters to Excel';
  }

  const onExport = useCallback(() => {
    if (inFlight.current) return;
    if (isMatrixView) {
      const written = exportMatrix(matrix.visibleRows, matrix.monthKeys, matrix.bands, matrix.year, matrix.yearLabel);
      if (written === 0) toast.info('No matrix rows to export');
      return;
    }
    inFlight.current = true;
    setExporting(true);
    exportTable().finally(() => {
      inFlight.current = false;
      setExporting(false);
    });
  }, [isMatrixView, matrix.visibleRows, matrix.monthKeys, matrix.bands, matrix.year, matrix.yearLabel, exportTable]);

  return { onExport, exporting, disabled, title };
}
